import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { CheckOutlined } from '@ant-design/icons';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import { Breadcrumb, Input, Button, message, Modal } from 'antd';
import SvgFormat from 'common/helpers/svgFormat';
import LeaveAlert from '~components/leave-alert';
import { withTranslation } from 'react-i18next';
import generatePath from '~settings/routing';
import * as landlordQueries from '~settings/queries';
import endpoints from '~settings/endpoints';
import { addWithdrawalMethod, updateWithdrawalMethod } from '~actions/withdrawal';
import { fetch } from '~helpers/graphql';
import {
  withdrawalMethods,
  withdrawalMethodIcons,
  getAllAppliedPropertySlugs,
  formatPayload,
} from '~helpers/withdrawal';
import PropertySelector from '~components/property-selector';
import Stripe from '~helpers/stripe';
import { emailRegex } from '~helpers/validate';
import BankTransfer from '~pages/withdrawal/edit/bank-transfer';
import { bookingJourneyType } from 'common/constants/booking';
import { getSafeHtml } from 'common/helpers/safeHtml';

const mapStateToProps = state => {
  const list = state.withdrawal.getIn(['list']);
  return {
    country: state.auth.getIn(['base', 'partnerCountry']),
    list: (list && list.toJS()) || [],
  };
};
const mapDispatchToProps = dispatch => ({
  addWithdrawalMethods: withdrawal => {
    dispatch(addWithdrawalMethod(withdrawal));
  },
  updateWithdrawalMethod: (index, withdrawal) => {
    dispatch(updateWithdrawalMethod(index, withdrawal));
  },
});
@connect(mapStateToProps, mapDispatchToProps)
@withTranslation()
class WithdrawalEdit extends React.Component {
  constructor(props) {
    super(props);
    const {
      list,
      match: { url, params },
    } = props;

    this.isCreate = url === '/withdrawal/create';
    this.initialValue = {};
    if (!this.isCreate) {
      const withdrawal = list.find(item => item.id === decodeURIComponent(params.id));
      if (!withdrawal) {
        props.history.push(generatePath('withdrawal.list'));
      } else {
        this.initialValue = withdrawal;
      }
    }
    this.excludePropertySlugs = getAllAppliedPropertySlugs(list, this.initialValue.id);
    this.stripe = new Stripe(this.getPropertyCurrency(this.initialValue.properties));
    this.state = {
      method: this.initialValue.methodType || withdrawalMethods.STRIPE,
      stripeId: {
        value: this.initialValue.stripes && this.initialValue.stripes.stripeId,
        valid: true,
      },
      ignoreLeaveAlert: false,
      saving: false,
    };
  }

  getPropertyCurrency = properties => {
    if (!properties || properties.length === 0) {
      return null;
    }

    return properties[0].currency;
  };

  haveTouched = () => {
    const { form } = this.props;
    const isNamePropertiesChanged = form.isFieldsTouched(['name', 'properties']);
    if (isNamePropertiesChanged) {
      return true;
    }
    if (this.state.method !== (this.initialValue.methodType || withdrawalMethods.STRIPE)) {
      return true;
    }
    if (this.state.method === withdrawalMethods.STRIPE) {
      return (
        this.state.stripeId.value !==
        (this.initialValue.stripes && this.initialValue.stripes.stripeId)
      );
    }
    if (this.state.method === withdrawalMethods.VIRTUAL_CREDIT_CARD) {
      return form.isFieldTouched('virtualCreditCard');
    }
    if (this.state.method === withdrawalMethods.BANK_TRANSFER && this.bankTransferForm) {
      return this.bankTransferForm.isFieldsTouched();
    }
    return false;
  };

  validate = () =>
    new Promise((resolve, reject) => {
      if (this.state.method === withdrawalMethods.STRIPE) {
        this.props.form.validateFields(['name', 'properties'], (err, values) => {
          this.setState({
            stripeId: { value: this.state.stripeId.value, valid: !!this.state.stripeId.value },
          });
          if (err || !this.state.stripeId.value) {
            reject();
          } else {
            resolve({
              name: values.name,
              properties: values.properties.map(item => ({ propertyId: item.id })),
              methodType: withdrawalMethods.STRIPE,
              stripes: {
                stripeId: this.state.stripeId.value,
              },
            });
          }
        });
      } else if (this.state.method === withdrawalMethods.VIRTUAL_CREDIT_CARD) {
        this.props.form.validateFields(
          ['name', 'properties', 'virtualCreditCard'],
          (err, values) => {
            if (err) {
              reject(err);
            } else {
              resolve({
                name: values.name,
                properties: values.properties.map(item => ({ propertyId: item.id })),
                methodType: withdrawalMethods.VIRTUAL_CREDIT_CARD,
                virtualCreditCards: {
                  email: values.virtualCreditCard,
                },
              });
            }
          },
        );
      } else if (this.state.method === withdrawalMethods.BANK_TRANSFER) {
        this.props.form.validateFields(['name', 'properties'], (err, values) => {
          this.bankTransferForm.validateFields((bankErr, bankValues) => {
            if (err || bankErr) {
              reject(err);
            } else {
              resolve({
                name: values.name,
                properties: values.properties.map(item => ({ propertyId: item.id })),
                methodType: withdrawalMethods.BANK_TRANSFER,
                bankTransfers: { data: formatPayload(bankValues) },
              });
            }
          });
        });
      }
    });

  handleSave = () => {
    if (this.state.saving) {
      return;
    }
    this.validate()
      .then(fields => {
        this.setState({
          saving: true,
        });
        if (this.isCreate) {
          fetch({
            url: endpoints.addWithdrawalMethod.url(),
            query: landlordQueries.addWithdrawalMethod({ input: fields }),
          })
            .then(res => {
              this.props.addWithdrawalMethods(res.createWithdrawMethod.withdrawMethod);
              message.success(this.props.t('landlord.withdrawal.create.create_success'));
              this.setState({ ignoreLeaveAlert: true }, this.handleCancel);
            })
            .catch(error => {
              if (error && error.message && error.message?.includes('PROPERTY_HAS_BEEN_BIND')) {
                message.error(
                  this.props.t('landlord.withdrawal.create.error.property_has_been_bind'),
                );
              } else if (error?.message === 'INVALID_PROPERTY_AP_CATEGORY') {
                Modal.error({
                  title: this.props.t('landlord.withdrawal.applied_properties.title'),
                  content: this.props.t(
                    'landlord.withdrawal.applied_properties.error_modal.content',
                  ),
                  okText: this.props.t(
                    'landlord.booking.reconciliation.details.stage.modal.confirm',
                  ),
                });
              } else {
                message.error(this.props.t('landlord.withdrawal.create.error'));
              }
            })
            .finally(() => {
              this.setState({
                saving: false,
              });
            });
        } else {
          fetch({
            url: endpoints.updateWithdrawalMethod.url(),
            query: landlordQueries.updateWithdrawalMethod({
              input: Object.assign({}, fields, { id: this.initialValue.id }),
            }),
          })
            .then(res => {
              const { withdrawMethod } = res.updateWithdrawMethod;
              const index = this.props.list.findIndex(item => item.id === withdrawMethod.id);
              this.props.updateWithdrawalMethod(index, withdrawMethod);
              message.success(this.props.t('landlord.withdrawal.create.update_success'));
              this.setState({ ignoreLeaveAlert: true }, this.handleCancel);
            })
            .catch(error => {
              if (error && error.message && error.message?.includes('PROPERTY_HAS_BEEN_BIND')) {
                message.error(
                  this.props.t('landlord.withdrawal.create.error.property_has_been_bind'),
                );
              } else if (error?.message === 'INVALID_PROPERTY_AP_CATEGORY') {
                Modal.error({
                  title: this.props.t('landlord.withdrawal.applied_properties.title'),
                  content: this.props.t(
                    'landlord.withdrawal.applied_properties.error_modal.content',
                  ),
                  okText: this.props.t(
                    'landlord.booking.reconciliation.details.stage.modal.confirm',
                  ),
                });
              } else {
                message.error(this.props.t('landlord.withdrawal.create.error'));
              }
            })
            .finally(() => {
              this.setState({
                saving: false,
              });
            });
        }
      })
      .catch(() => {
        // Do nothing
      });
  };

  getStripeId = () => {
    this.stripe
      .getStripeId()
      .then(stripeId => {
        this.setState({
          stripeId: {
            value: stripeId,
            valid: !!stripeId,
          },
        });
      })
      .catch(e => {
        message.error(`Error happened when bind stripe: ${e && e.message}`);
      });
  };

  handleStripeAuth = () => {
    const propertiesList = this.props.form.getFieldValue('properties');
    if (!propertiesList || propertiesList.length === 0) {
      this.propertySelector.openToSelect().then(({ select, properties }) => {
        if (select && properties && properties.length > 0) {
          this.handlePropertiesChange(properties);
          this.getStripeId();
        }
      });
    } else {
      this.getStripeId();
    }
  };

  handlePropertiesChange = properties => {
    const currentCurrency = this.getPropertyCurrency(properties);
    if (
      !properties ||
      properties.length === 0 ||
      this.stripe.getStripeCurrency() !== currentCurrency
    ) {
      this.setState({
        stripeId: {
          value: null,
          valid: true,
        },
      });
      this.stripe.setStripeCurrency(currentCurrency);
    }
  };

  renderStripe = () => {
    const { t } = this.props;
    return (
      <div>
        <div className="withdrawal-edit__method-desc">
          {getSafeHtml(t('landlord.withdrawal.create.stripe.desc'))}
        </div>
        <Choose>
          <When condition={this.state.stripeId.value}>
            <div className="withdrawal-edit__stripe-authed">
              <span className="withdrawal-edit__stripe-email">{this.state.stripeId.value}</span>
              <div className="withdrawal-edit__stripe-tick-icon">
                <CheckOutlined />
              </div>
              <span className="withdrawal-edit__stripe-authed-text">
                {t('landlord.withdrawal.create.stripe.authorized')}
              </span>
              <a
                onClick={this.handleStripeAuth}
                role="presentation"
                className="withdrawal-edit__stripe-reauth"
              >
                {t('landlord.withdrawal.create.stripe.reauthorize')}
              </a>
            </div>
          </When>
          <Otherwise>
            <Button
              size="default"
              type="primary"
              className="withdrawal-edit__stripe-set-up"
              onClick={this.handleStripeAuth}
            >
              {t('landlord.withdrawal.create.stripe.set_up')}
            </Button>
            <If condition={!this.state.stripeId.valid}>
              <div className="withdrawal-edit__stripe-error">
                {t('landlord.withdrawal.create.stripe.error')}
              </div>
            </If>
          </Otherwise>
        </Choose>
      </div>
    );
  };

  renderVirtualCreditCard = () => {
    const {
      t,
      form: { getFieldDecorator },
    } = this.props;
    return (
      <div className="withdrawal-edit__vcc">
        <div className="withdrawal-edit__method-desc">
          {getSafeHtml(t('landlord.withdrawal.create.vcc.desc'))}
        </div>
        <div className="withdrawal-edit__field-title withdrawal-edit__field-title--required">
          {t('landlord.withdrawal.create.vcc.email')}
        </div>
        <Form.Item required>
          {getFieldDecorator('virtualCreditCard', {
            initialValue:
              this.initialValue.virtualCreditCards && this.initialValue.virtualCreditCards.email,
            validateTrigger: 'onBlur',
            rules: [
              {
                required: true,
                pattern: emailRegex,
                message: t('landlord.withdrawal.create.bank_transfer.common_error', {
                  field: t('landlord.withdrawal.create.vcc.email'),
                }),
              },
              {
                max: 255,
                message: t('landlord.withdrawal.create.bank_transfer.number_letter_error.max', {
                  number: 255,
                }),
              },
            ],
          })(
            <Input
              className="withdrawal-edit__field-content"
              placeholder={t('landlord.withdrawal.create.bank_transfer.common_placeholder', {
                field: t('landlord.withdrawal.create.vcc.email'),
              })}
            />,
          )}
        </Form.Item>
      </div>
    );
  };

  handleChangeMethod = method => {
    this.setState({
      method,
    });
  };

  handleCancel = () => {
    this.props.history.push(generatePath('withdrawal.list'));
  };

  render() {
    const {
      t,
      country,
      form: { getFieldDecorator },
      history,
    } = this.props;
    return (
      <div className="withdrawal-edit">
        <LeaveAlert
          history={history}
          t={t}
          titleText={t('landlord.leave_alert.title')}
          content={t('landlord.leave_alert.content')}
          okText={t('landlord.leave_alert.ok')}
          cancelText={t('landlord.leave_alert.cancel')}
          when={this.state.ignoreLeaveAlert ? false : this.haveTouched()}
          closeAlertContent={t('landlord.leave_alert.content')}
        />
        <div className="withdrawal-edit__header">
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/withdrawal">{t('landlord.withdrawal.breadcrumb.receive_mothod')}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {t(
                `landlord.withdrawal.breadcrumb.${
                  this.isCreate ? 'create_receive_mothod' : 'edit_receive_mothod'
                }`,
              )}
            </Breadcrumb.Item>
          </Breadcrumb>
          <h3 className="withdrawal-edit__title">
            {t(
              `landlord.withdrawal.breadcrumb.${
                this.isCreate ? 'create_receive_mothod' : 'edit_receive_mothod'
              }`,
            )}
          </h3>
        </div>
        <div className="withdrawal-edit__content">
          <div className="withdrawal-edit__form">
            <div className="withdrawal-edit__field">
              <div className="withdrawal-edit__field-title withdrawal-edit__field-title--required">
                {t('landlord.withdrawal.create.name')}
              </div>
              <Form.Item required>
                {getFieldDecorator('name', {
                  initialValue: this.initialValue.name,
                  validateTrigger: 'onBlur',
                  rules: [
                    {
                      required: true,
                      message: t('landlord.withdrawal.create.bank_transfer.common_error', {
                        field: t('landlord.withdrawal.create.name'),
                      }),
                    },
                    {
                      max: 30,
                      message: t('landlord.withdrawal.create.bank_transfer.number_error.max', {
                        number: 30,
                      }),
                    },
                  ],
                })(
                  <Input
                    className="withdrawal-edit__field-content"
                    placeholder={t('landlord.withdrawal.create.bank_transfer.common_placeholder', {
                      field: t('landlord.withdrawal.create.name'),
                    })}
                  />,
                )}
              </Form.Item>
            </div>
            <div className="withdrawal-edit__field">
              <div className="withdrawal-edit__field-title">
                {t('landlord.withdrawal.create.choose_method')}
              </div>
              <div className="withdrawal-edit__method-list">
                <For each="method" of={Object.keys(withdrawalMethods)} index="idx">
                  <div
                    className={classNames('withdrawal-edit__method-item', {
                      'withdrawal-edit__method-item--active': method === this.state.method,
                    })}
                    key={method}
                    onClick={() => {
                      this.handleChangeMethod(method);
                    }}
                    role="presentation"
                  >
                    <SvgFormat
                      className="withdrawal-edit__method-item-icon"
                      type={withdrawalMethodIcons[method]}
                    />
                    <div className="withdrawal-edit__method-item-text">
                      {t(`landlord.withdrawal.create.${method.toLowerCase()}.title`)}
                    </div>
                    <If condition={method === this.state.method}>
                      <div className="withdrawal-edit__method-tick">
                        <SvgFormat type="tick" className="withdrawal-edit__method-tick-icon" />
                      </div>
                    </If>
                  </div>
                </For>
              </div>
              <div
                className={`withdrawal-edit__method-detail withdrawal-edit__method-detail--${this.state.method
                  .toLowerCase()
                  .replace(/_/g, '-')}`}
              >
                <Choose>
                  <When condition={this.state.method === withdrawalMethods.STRIPE}>
                    {this.renderStripe()}
                  </When>
                  <When condition={this.state.method === withdrawalMethods.VIRTUAL_CREDIT_CARD}>
                    {this.renderVirtualCreditCard()}
                  </When>
                  <When condition={this.state.method === withdrawalMethods.BANK_TRANSFER}>
                    <BankTransfer
                      data={
                        (this.initialValue.bankTransfers && this.initialValue.bankTransfers.data) ||
                        {}
                      }
                      t={t}
                      country={country}
                      form={this.props.form}
                      ref={form => {
                        this.bankTransferForm = form;
                      }}
                    />
                  </When>
                </Choose>
              </div>
            </div>
            <div className="withdrawal-edit__field">
              <div className="withdrawal-edit__field-title  withdrawal-edit__field-title--required">
                {t('landlord.withdrawal.applied_properties.title')}
              </div>
              <div className="withdrawal-edit__field-desc">
                {t('landlord.withdrawal.create.applied_properties.desc')}
              </div>
              <Form.Item>
                {getFieldDecorator('properties', {
                  initialValue: this.initialValue.properties,
                  valuePropName: 'propertiesSelected',
                  rules: [
                    {
                      required: true,
                      min: 1,
                      type: 'array',
                      message: t('landlord.withdrawal.create.bank_transfer.common_select_error', {
                        field: t('landlord.withdrawal.applied_properties.title').toLowerCase(),
                      }),
                    },
                  ],
                })(
                  <PropertySelector
                    onChange={this.handlePropertiesChange}
                    className="withdrawal-edit__field-content"
                    propertiesSlugsExcluded={this.excludePropertySlugs}
                    onlyOneCountry
                    propertiesType={[
                      bookingJourneyType.INSTANT_BOOK,
                      bookingJourneyType.SEMI_AUTOMATIC,
                      bookingJourneyType.MANUAL,
                    ]}
                    ref={element => {
                      this.propertySelector = element;
                    }}
                    needValidatePayment={this.state.method === withdrawalMethods.STRIPE}
                    t={t}
                  />,
                )}
              </Form.Item>
            </div>
          </div>
          <div className="withdrawal-edit__footer">
            <Button
              size="default"
              type="primary"
              className="withdrawal-edit__footer-icon"
              loading={this.state.saving}
              onClick={this.handleSave}
            >
              {this.isCreate
                ? t('landlord.withdrawal.create.button.create')
                : t('landlord.withdrawal.create.button.save')}
            </Button>
            <Button
              size="default"
              type="default"
              className="withdrawal-edit__footer-icon"
              onClick={this.handleCancel}
            >
              {t('landlord.withdrawal.create.button.cancel')}
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

WithdrawalEdit.propTypes = {
  t: PropTypes.func,
  history: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      virtualCreditCard: PropTypes.string,
      stripeId: PropTypes.string,
      bankTransfer: PropTypes.object,
    }),
  ),
  country: PropTypes.string,
  form: PropTypes.object.isRequired,
  updateWithdrawalMethod: PropTypes.func,
  addWithdrawalMethods: PropTypes.func,
};

WithdrawalEdit.defaultProps = {
  t: () => {},
  withdrawal: {},
  form: {
    getFieldDecorator: () => {},
  },
  list: [],
  country: '',
  updateWithdrawalMethod: () => {},
  addWithdrawalMethods: () => {},
};

export default Form.create({ name: 'withdrawalEdit' })(WithdrawalEdit);
